import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { router } from '@inertiajs/react';

interface Document {
	id: number;
	original_filename: string;
}

interface Props {
	valuationId: number;
	category: string;
	documents: Document[];
	readOnly?: boolean;
}

const DocumentUploader = ({ valuationId, category, documents, readOnly }: Props) => {
	const [files, setFiles] = useState<FileList | null>(null);
	const [uploading, setUploading] = useState(false);
	const [removeDoc, setRemoveDoc] = useState<Document | null>(null);
	const [removing, setRemoving] = useState(false);

	const baseUrl = `/valuation-form/${valuationId}`;

	const handleUpload = () => {
		if (!files || files.length === 0) return;

		const formData = new FormData();
		formData.append('category', category);
		Array.from(files).forEach(file => formData.append('files[]', file));

		setUploading(true);
		router.post(`${baseUrl}/documents`, formData as any, {
			forceFormData: true,
			preserveScroll: true,
			onFinish: () => {
				setUploading(false);
				setFiles(null);
			},
		});
	};

	const handleRemove = () => {
		if (!removeDoc) return;
		setRemoving(true);
		router.delete(`${baseUrl}/documents/${removeDoc.id}`, {
			preserveScroll: true,
			onFinish: () => {
				setRemoving(false);
				setRemoveDoc(null);
			},
		});
	};

	return (
		<div>
			{documents.length > 0 && (
				<div className="mb-3">
					{documents.map(doc => (
						<div key={doc.id} className="d-flex align-items-center gap-2 mb-2">
							<span className="text-truncate" style={{ maxWidth: '300px' }}>{doc.original_filename}</span>
							<a
								href={`${baseUrl}/documents/${doc.id}/file`}
								target="_blank"
								rel="noopener noreferrer"
								className="btn btn-sm btn-outline-primary"
							>
								View
							</a>
							{!readOnly && (
								<Button
									variant="outline-danger"
									size="sm"
									onClick={() => setRemoveDoc(doc)}
								>
									Remove
								</Button>
							)}
						</div>
					))}
				</div>
			)}

			{!readOnly && (
				<div>
					<div className="d-flex align-items-center gap-2">
						<Form.Control
							type="file"
							accept=".pdf"
							multiple
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiles(e.target.files)}
						/>
						<Button size="sm" disabled={!files || files.length === 0 || uploading} onClick={handleUpload}>
							{uploading ? 'Uploading…' : 'Upload'}
						</Button>
					</div>
					<Form.Text className="text-muted">PDF only, maximum 20 MB each.</Form.Text>
				</div>
			)}

			{readOnly && documents.length === 0 && (
				<p className="text-muted mb-0">No documents uploaded.</p>
			)}

			<Modal show={!!removeDoc} onHide={() => setRemoveDoc(null)}>
				<Modal.Header closeButton>
					<Modal.Title>Remove Document</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Are you sure you want to remove{' '}
					<strong>{removeDoc?.original_filename}</strong>?
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setRemoveDoc(null)}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleRemove} disabled={removing}>
						{removing ? 'Removing…' : 'Yes, Remove'}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default DocumentUploader;
